/**
 * Dashboard Layout
 * Industry-standard responsive layout with Tailwind CSS
 * Mobile, Tablet, Desktop support with smooth animations
 */

import * as React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useTheme } from '@hooks/useTheme';
import { useWebSocket } from '@hooks/useWebSocket';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '@services/api';
import MobileDrawer from '../components/layout/MobileDrawer';
import RealtimeNotifications from '../components/layout/RealtimeNotifications';
import UniversalSearchBar from '../components/layout/UniversalSearchBar';
import CompanyDataSourceSelector from '../components/layout/CompanyDataSourceSelector';
import {
  LayoutDashboard,
  BarChart3,
  Users,
  UserCog,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  TrendingUp,
  Activity,
  Mail,
  Target,
  DollarSign,
  FileText,
  Send,
  ShoppingCart,
  FileSearch,
} from 'lucide-react';

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { darkMode, toggleTheme } = useTheme();
  const { connected: wsConnected } = useWebSocket();
  const { selectedCompany } = useDataSourceStore();

  // Check if BetterStack module is available
  const { data: companyData } = useQuery({
    queryKey: ['company', selectedCompany?._id],
    queryFn: async () => {
      if (!selectedCompany?._id) return null;
      const response = await api.get(`/api/v1/companies/${selectedCompany._id}`);
      return response.data.data;
    },
    enabled: !!selectedCompany?._id,
    staleTime: 5 * 60 * 1000,
  });

  const hasBetterStackModule = companyData?.dataSources?.some(
    (ds: any) => ds.isActive && ds.availableModules?.includes('betterstack')
  );

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  // Helper function to check if user has permission for a resource
  const hasPermission = (resource: string, action: string = 'read'): boolean => {
    if (!user || !user.roles || user.roles.length === 0) {
      return false;
    }
    
    // Check if roles are just strings (IDs) instead of objects - this shouldn't happen now
    if (typeof user.roles[0] === 'string') {
      console.error('❌ Roles not populated. Please log out and log in again.');
      return false;
    }
    
    // Super admin check - if user has * resource with * action
    const isSuperAdmin = user.roles.some((role: any) => 
      role.permissions?.some((perm: any) => 
        perm.resource === '*' && perm.actions.includes('*')
      )
    );
    
    if (isSuperAdmin) return true;
    
    // Check specific permission
    return user.roles.some((role: any) =>
      role.permissions?.some((perm: any) =>
        (perm.resource === resource || perm.resource === '*') &&
        (perm.actions.includes(action) || perm.actions.includes('*'))
      )
    );
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboards' },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, permission: 'analytics' },
    { name: 'Funnel Analysis', href: '/analytics/funnel', icon: TrendingUp, permission: 'funnel' },
    { name: 'Attribution', href: '/analytics/attribution', icon: Target, permission: 'attribution' },
    { name: 'ROAS Forecast', href: '/analytics/roas', icon: DollarSign, permission: 'roas' },
    { name: 'Sessions', href: '/sessions', icon: Activity, permission: 'sessions' },
    { name: 'Email Campaigns', href: '/email-campaigns', icon: Mail, permission: 'email-campaigns' },
    { name: 'Email Logs', href: '/email-logs', icon: FileText, permission: 'email-logs' },
    { name: 'Email Marketing', href: '/email-marketing', icon: Send, permission: 'getresponse' },
    { name: 'E-shops', href: '/eshops', icon: ShoppingCart, permission: 'eshop' },
    ...(hasBetterStackModule && hasPermission('betterstack')
      ? [{ name: 'BetterStack', href: '/betterstack', icon: FileSearch, permission: 'betterstack' }]
      : []),
  ].filter(item => !item.permission || hasPermission(item.permission));

  const adminNavigation = [
    { name: 'Users', href: '/admin/users', icon: Users, permission: 'users' },
    { name: 'Roles', href: '/admin/roles', icon: UserCog, permission: 'roles' },
    { name: 'Companies', href: '/admin/companies', icon: Building2, permission: 'companies' },
  ].filter(item => !item.permission || hasPermission(item.permission));

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen h-screen flex overflow-hidden bg-gray-50 dark:bg-gray-900 w-full">
      {/* Mobile Drawer */}
      <MobileDrawer isOpen={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} />

      {/* Desktop Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } hidden lg:flex bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
        transition-all duration-300 ease-in-out flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
          {sidebarOpen && (
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Analytics
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-1">
            {navigation.map(item => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span className="font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>

          {/* Admin Section */}
          {adminNavigation.length > 0 && (
            <div className="mt-8">
              {sidebarOpen && (
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Admin
                </h3>
              )}
              <div className="space-y-1">
                {adminNavigation.map(item => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon size={20} />
                    {sidebarOpen && <span className="font-medium">{item.name}</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Settings */}
          <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/settings"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                isActive('/settings')
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <Settings size={20} />
              {sidebarOpen && <span className="font-medium">Settings</span>}
            </Link>
          </div>
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-8 h-8 min-w-[2rem] min-h-[2rem] rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shrink-0 aspect-square">
                {user?.firstName[0]}
                {user?.lastName[0]}
              </div>
              {sidebarOpen && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </button>

            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut size={16} />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 w-full">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 lg:px-6 shrink-0">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 mr-3 transition-colors"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>

          {/* Page Title - Mobile Only */}
          <div className="flex-1 lg:hidden min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
              {navigation.find(item => isActive(item.href))?.name ||
                adminNavigation.find(item => isActive(item.href))?.name ||
                'Dashboard'}
            </h2>
          </div>

          {/* Company & Data Source Selector - Only for Admin/Super Admin */}
          {hasPermission('companies', 'read') && (
            <div className="hidden lg:flex items-center gap-3 mr-4">
              <CompanyDataSourceSelector />
            </div>
          )}

          {/* Search Bar - Desktop */}
          <div className="hidden lg:flex flex-1 mx-4">
            <UniversalSearchBar />
          </div>

          <div className="flex items-center gap-2 lg:gap-3">
            {/* WebSocket Status */}
            <div className="hidden lg:flex items-center gap-2 text-xs">
              <div
                className={`w-2 h-2 rounded-full ${
                  wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}
              />
              <span className="text-gray-600 dark:text-gray-400">
                {wsConnected ? 'Live' : 'Offline'}
              </span>
            </div>

            {/* Notifications */}
            <RealtimeNotifications />

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </header>

        {/* Mobile Search Bar & Selector */}
        <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 py-2 space-y-2 shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto">
            <CompanyDataSourceSelector />
          </div>
          <UniversalSearchBar />
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
